import { SCRIM_GRADIENT_HEAVY } from '@/components/page-builder/blocks/scrimGradient';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { AuthorByline } from './AuthorByline';
import { articleUrl, itemVariants } from './constants';
import type { JournalGuide } from './types';

interface EditorsPicksProps {
    guides: JournalGuide[];
}

export default function EditorsPicks({ guides }: EditorsPicksProps) {
    const shouldReduceMotion = useReducedMotion();

    if (guides.length === 0) return null;

    const [feature, ...rest] = guides;
    const supporting = rest.slice(0, 3);
    const featureTag = feature.kicker ?? feature.country_region ?? feature.tags?.[0]?.name;

    return (
        <motion.section
            id="editors-picks"
            className="lux-space-px scroll-mt-28 bg-[#0b2222] md:scroll-mt-32"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={shouldReduceMotion ? undefined : { hidden: {}, visible: { transition: { duration: 0.6 } } }}
        >
            <div className="mx-auto max-w-[1400px] py-20 lg:py-28">
                <motion.div className="mb-16 text-center" variants={shouldReduceMotion ? undefined : itemVariants}>
                    <div className="mx-auto mb-5 flex items-center justify-center gap-4">
                        <div className="h-px w-16 bg-[#F5F5F5]/20" />
                        <svg width="6" height="6" viewBox="0 0 6 6" className="shrink-0 text-[#F5F5F5]/30">
                            <rect x="3" y="0" width="4.24" height="4.24" rx="0.5" transform="rotate(45 3 0)" fill="currentColor" />
                        </svg>
                        <div className="h-px w-16 bg-[#F5F5F5]/20" />
                    </div>
                    <h2 className="lux-didot lux-didot-xl text-[#F5F5F5]">Editor&rsquo;s Picks</h2>
                    <div className="mx-auto mt-5 flex items-center justify-center gap-4">
                        <div className="h-px w-16 bg-[#F5F5F5]/20" />
                        <svg width="6" height="6" viewBox="0 0 6 6" className="shrink-0 text-[#F5F5F5]/30">
                            <rect x="3" y="0" width="4.24" height="4.24" rx="0.5" transform="rotate(45 3 0)" fill="currentColor" />
                        </svg>
                        <div className="h-px w-16 bg-[#F5F5F5]/20" />
                    </div>
                </motion.div>

                <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
                    {/* Feature pick — tall card with image overlay */}
                    <motion.div variants={shouldReduceMotion ? undefined : itemVariants}>
                        <Link href={articleUrl(feature.slug)} className="group relative block aspect-[3/4] overflow-hidden sm:aspect-[4/5] lg:h-full">
                            {feature.main_image && (
                                <img
                                    src={feature.main_image}
                                    alt={feature.title}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                            )}
                            <div className="absolute inset-0" style={{ background: SCRIM_GRADIENT_HEAVY }} />
                            <div className="absolute inset-x-0 bottom-0 p-6 lg:p-8">
                                {featureTag && <span className="lux-caps lux-caps-sm mb-2 block text-white/60">{featureTag}</span>}
                                <h3 className="lux-didot lux-didot-xl text-white">{feature.title}</h3>
                                {feature.excerpt && <p className="lux-body lux-body-sm mt-3 line-clamp-2 text-white/75">{feature.excerpt}</p>}
                                {feature.author_name && <AuthorByline name={feature.author_name} className="mt-4 text-white/70" />}
                            </div>
                        </Link>
                    </motion.div>

                    {/* Supporting picks — stacked horizontal cards */}
                    <div className="flex flex-col justify-center divide-y divide-[#F5F5F5]/10">
                        {supporting.map((guide) => {
                            const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;
                            return (
                                <motion.div
                                    key={guide.id}
                                    className="py-6 first:pt-0 last:pb-0"
                                    variants={shouldReduceMotion ? undefined : itemVariants}
                                >
                                    <Link href={articleUrl(guide.slug)} className="group flex gap-5">
                                        {guide.main_image && (
                                            <div className="relative aspect-[4/3] w-32 shrink-0 overflow-hidden sm:w-40">
                                                <img
                                                    src={guide.main_image}
                                                    alt={guide.title}
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className="flex min-w-0 flex-col justify-center">
                                            {tag && <span className="lux-caps lux-caps-sm mb-1 text-[#F5F5F5]/50">{tag}</span>}
                                            <h3 className="lux-didot lux-didot-md text-[#F5F5F5] transition-colors group-hover:text-[#F5F5F5]/80">
                                                {guide.title}
                                            </h3>
                                            {guide.excerpt && (
                                                <p className="lux-body lux-body-sm mt-2 line-clamp-2 text-[#F5F5F5]/60">{guide.excerpt}</p>
                                            )}
                                            {guide.author_name && <AuthorByline name={guide.author_name} className="mt-3 text-[#F5F5F5]/55" />}
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </motion.section>
    );
}
